import { useAppSelector, useTranslatedTag, useTranslation } from "@next-platform/client-common-core";
import { Button, ButtonSize, Image, Modal, ModalSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useEffect, useState } from "react";

import { SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY } from "@/constants";
import { cn } from "@/utils";

import { Row } from "../customComponents";
import Trans from "../Trans/Trans";
import { FTUE_MODAL_DELAY, FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY, preferenceOptions } from "./constants";
import { FtueModalProps, FtuePreference } from "./types";

/** First Time User Experience modal — gender preference selection shown on initial login. */
export default function FtueModal({ onConfirmation, dataTestId = "" }: FtueModalProps) {
  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const [stateIsFtueModalOpen, setStateIsFtueModalOpen] = useState(false);

  const [stateSelectedPreference, setStateSelectedPreference] = useState<FtuePreference[]>([]);
  const isAnyPreferenceSelected = stateSelectedPreference.length > 0;
  const isOverlayLogin = useAppSelector((state) => state.navigation.isOverlayLogin);
  const shouldShowOnboardingModal = useAppSelector((state) => state.navigation.showOnboardingModal);

  useEffect(() => {
    const hasSearchParams = window.location.search.length > 0;

    if (hasSearchParams) {
      sessionStorage.removeItem(FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY);
      return;
    }

    const shouldShowFtueModal = localStorage.getItem(SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY) === "true";

    if (!shouldShowFtueModal) {
      return;
    }

    sessionStorage.setItem(FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY, "true");
  }, []);

  useEffect(() => {
    const isFtuePendingDisplay = sessionStorage.getItem(FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY) === "true";

    if (!isFtuePendingDisplay) {
      return;
    }

    const timer = setTimeout(() => {
      if (window.location.search.length > 0 || sessionStorage.getItem(FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY) !== "true") {
        return;
      }

      setStateIsFtueModalOpen(true);
    }, FTUE_MODAL_DELAY);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (stateIsFtueModalOpen && !isOverlayLogin && !shouldShowOnboardingModal) {
      localStorage.setItem(SHOULD_SHOW_FTUE_MODAL_LOCAL_STORAGE_KEY, "false");
      sessionStorage.removeItem(FTUE_PENDING_DISPLAY_SESSION_STORAGE_KEY);
    }
  }, [stateIsFtueModalOpen, isOverlayLogin, shouldShowOnboardingModal]);

  if (!stateIsFtueModalOpen || isOverlayLogin || shouldShowOnboardingModal) {
    return null;
  }

  const handleSelection = (preference: FtuePreference, isSelected: boolean) => {
    setStateSelectedPreference((prev) => (isSelected ? prev.filter((pref) => pref !== preference) : [...prev, preference]));
  };

  const handleConfirmation = () => {
    onConfirmation(stateSelectedPreference);
    setStateIsFtueModalOpen(false);
  };

  return (
    <Modal
      title={t("user:ftue.modal.title")}
      open={true}
      onOpenChange={setStateIsFtueModalOpen}
      size={ModalSize.LARGE}
      className="!gap-0 dark:!bg-gray-6"
    >
      <div className="grid grid-cols-3 pt-md *:w-full *:h-[148px] gap-md *:rounded-md" data-testid={dataTestId}>
        {preferenceOptions.map(({ key, image, label }) => {
          const isSelected = stateSelectedPreference.includes(key);
          const tagLabel = getTranslatedTag(label);
          return (
            <div key={key} className={`relative cursor-pointer transition-opacity duration-200`} onClick={() => handleSelection(key, isSelected)}>
              <Image
                src={image}
                alt={tagLabel}
                className={cn("w-full h-full object-cover rounded-md", !isSelected ? "opacity-50" : "ring-4 ring-blue-10")}
              />
              <Row className="absolute bottom-0 w-full p-sm justify-center">
                <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM} className="text-white">
                  {tagLabel}
                </Text>
              </Row>
            </div>
          );
        })}
      </div>
      <Button onClick={handleConfirmation} size={ButtonSize.LARGE}>
        {isAnyPreferenceSelected ? <Trans i18nKey="user:ftue.modal.cta.startChatting" /> : <Trans i18nKey="user:ftue.modal.cta.jumpIn" />}
      </Button>
    </Modal>
  );
}
